import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import { Arrow, Circle, Group, Line } from "react-konva";
import Konva from "konva";
import { Html } from "react-konva-utils";
import PropTypes from "prop-types";
import "../styles.css";
import {
  FIXED_OFF_CANVAS_SIDE_WIDTH_PX,
  getMainCanvasWidthPx,
} from "../../utils/mainCanvasLayout";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";
import {
  getAbsoluteHorizontalExtent,
  getOffCanvasMainColumnBounds,
  getOffCanvasHorizontalStripSide,
} from "../../utils/boundaryUtils";
import { constrainOffCanvasPosition } from "../../utils/dragEndUtils";

// Constants
const MIN_WIDTH = 30;
const MIN_HEIGHT = 0;
const MAX_HEIGHT = 600;
const MAIN_STAGE_EDGE_BUFFER_PX = 10;
// DRAG THROTTLING: Match imageRenderer behavior to prevent fluctuation when images are present
// Reduced from 80ms to 16ms for better cross-canvas drag performance
const DRAG_STATE_THROTTLE_MS = 16;
let _lastUnifiedElementDragStateTime = 0;

// Utility functions
function normalizeToGroup(points = []) {
  let minX = Infinity,
    minY = Infinity;
  for (let i = 0; i < points.length; i += 2) {
    minX = Math.min(minX, points[i]);
    minY = Math.min(minY, points[i + 1]);
  }

  const relativePoints = points.map((val, idx) =>
    idx % 2 === 0 ? val - minX : val - minY,
  );

  return { relativePoints, offsetX: minX, offsetY: minY };
}

const calculateLengthAndAngle = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  return { length, angle };
};

// CRITICAL: For arrows (single or double-sided), points must have non-zero length or both heads appear at the same place.
// Return a valid non-zero-length points array when the given points are missing or zero-length.
function ensureNonZeroLengthPointsForArrow(
  points,
  element,
  defaultLength = 100,
) {
  if (!points || points.length < 4) {
    const len = element?.width > 0 ? element.width : defaultLength;
    return [0, 0, len, 0];
  }
  const [x1, y1, x2, y2] = points;
  const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
  if (length < 1) {
    const len = element?.width > 0 ? element.width : defaultLength;
    return [0, 0, len, 0];
  }
  return points;
}

const UnifiedLineArrowElement = ({
  element,
  isDraggable,
  onClick,
  onDoubleClick,
  onUpdate,
  strokeColor,
  onDragEnd,
  saveToHistory,
  forwardRef,
  isSelected,
  onSelect,
  onDragStart,
  onDragMove,
  onTransform,
  onTransformEnd,
  hideBlueCircles = false,
  hitStrokeWidth = 20,
  setElementState,
  isOffCanvas = false,
  sourceStageRef,
  offCanvasStageRef,
  mainStageRef, // Main canvas stage ref (for coordinate conversion)
  allowOffCanvasTransfer = true,
  effectiveZoomLevel = 1,
  isPreview = false,
  onCopyToOppositeCanvas,
  canvasWidth,
  canvasHeight,
  onDotDragMove,
  updateElementProperties,
  lockedAction = null, // Locked action (for clone mode)
  isCloneMode = false, // Clone mode flag
  isMultiSelectMode = false, // Multi-select mode flag
  selectedObjectsArray = [], // Array of selected objects for multi-select
  forceShowBlueCircles = false, // Force-show blue orbs (e.g. for cross-canvas preview ghost)
  forceHideBlueCircles = false, // Force-hide blue orbs (e.g. when source element is hidden during preview)
}) => {
  const maxLineLayoutWidth = Math.max(
    MIN_WIDTH,
    Number(canvasWidth) || getMainCanvasWidthPx(),
  );
  // Determine arrow type based on checkboxes
  // pointerAtBeginning and pointerAtEnding control the arrow heads:
  // - Both false: Line
  // - pointerAtEnding=true only: Single-sided arrow (end only)
  // - pointerAtBeginning=true only: Single-sided arrow (start only)
  // - Both true: Double-sided arrow (both ends)
  // Explicitly check for true and ensure false/undefined/null values are treated as false

  // CRITICAL: Use state to track arrow flags so we can force updates when they change
  // This ensures React re-renders even if the element prop reference doesn't change
  const [arrowFlags, setArrowFlags] = useState(() => ({
    hasStartArrow: element.hasStartArrow === true,
    hasEndArrow: element.hasEndArrow === true,
  }));

  // CRITICAL: Use a ref to track the raw values to detect changes even if prop reference doesn't change
  const prevFlagsRef = useRef({
    hasStartArrow: element.hasStartArrow,
    hasEndArrow: element.hasEndArrow,
    elementId: element.id,
  });

  // CRITICAL: Match CircleElement - no cross-canvas position locking. Use element.x / element.y for Group position.
  const lastMainStagePositionRef = useRef({
    x: element.x ?? 0,
    y: element.y ?? 0,
  });
  const crossCanvasLockedPositionRef = useRef(null); // kept for ref cleanup in handleDragEnd
  /** "left" | "right" | null — locked for the current off-canvas drag so lines/arrows cannot jump strips */
  const offCanvasDragStripSideRef = useRef(null);

  // Sync arrow flags with element prop changes
  // CRITICAL: Check both the boolean values AND the raw values to catch all changes
  // Also check element.type as it changes when flags change
  useEffect(() => {
    const newHasStartArrow = element.hasStartArrow === true;
    const newHasEndArrow = element.hasEndArrow === true;
    const newHasStartArrowRaw = element.hasStartArrow;
    const newHasEndArrowRaw = element.hasEndArrow;
    const newType = element.type;

    // Check if flags actually changed by comparing with previous raw values
    const prevHasStartArrowRaw = prevFlagsRef.current.hasStartArrow;
    const prevHasEndArrowRaw = prevFlagsRef.current.hasEndArrow;
    const prevElementId = prevFlagsRef.current.elementId;

    // CRITICAL: Update if raw values changed OR if boolean interpretation changed OR if element ID changed
    // This catches cases where the value changes from true to false or vice versa
    const hasStartArrowChanged = prevHasStartArrowRaw !== newHasStartArrowRaw;
    const hasEndArrowChanged = prevHasEndArrowRaw !== newHasEndArrowRaw;
    const elementIdChanged = prevElementId !== element.id;
    const booleanInterpretationChanged =
      (prevHasStartArrowRaw === true) !== newHasStartArrow ||
      (prevHasEndArrowRaw === true) !== newHasEndArrow;

    // CRITICAL: Always update state if anything changed, even if it's the same boolean value
    // This ensures React re-renders when the prop changes
    if (
      hasStartArrowChanged ||
      hasEndArrowChanged ||
      booleanInterpretationChanged ||
      elementIdChanged
    ) {
      setArrowFlags({
        hasStartArrow: newHasStartArrow,
        hasEndArrow: newHasEndArrow,
      });

      // Update ref for next comparison
      prevFlagsRef.current = {
        hasStartArrow: newHasStartArrowRaw,
        hasEndArrow: newHasEndArrowRaw,
        elementId: element.id,
      };
    }
  }, [element.hasStartArrow, element.hasEndArrow, element.id, element.type]);

  // Use state values for arrow flags
  const elementHasStartArrow = arrowFlags.hasStartArrow;
  const elementHasEndArrow = arrowFlags.hasEndArrow;

  // Debug log to track flag changes

  // Only use type-based defaults if arrow flags are completely unset (undefined)
  // This is for backward compatibility with elements that might not have explicit flags
  // CRITICAL: Check both hasStartArrow/hasEndArrow AND legacy pointerAtBeginning/pointerAtEnd
  const hasExplicitFlags =
    element.hasStartArrow !== undefined ||
    element.hasEndArrow !== undefined ||
    element.pointerAtBeginning !== undefined ||
    element.pointerAtEnd !== undefined;

  let hasStartArrow = elementHasStartArrow;
  let hasEndArrow = elementHasEndArrow;

  if (!hasExplicitFlags) {
    // CRITICAL: Normalize element type to handle both lowercase and uppercase
    const normalizedElementType = element.type
      ? element.type.toLowerCase() === "arrow"
        ? "Arrow"
        : element.type.toLowerCase() === "double_sided_arrow" ||
            element.type === "DoubleSidedArrow"
          ? "DoubleSidedArrow"
          : element.type
      : null;

    // Only apply type-based defaults if flags are completely unset
    if (normalizedElementType === "Arrow") {
      // Default arrows to have pointer at end only
      hasEndArrow = true;
      hasStartArrow = false;
    } else if (normalizedElementType === "DoubleSidedArrow") {
      // Default double-sided arrows to have pointers at both ends
      hasStartArrow = true;
      hasEndArrow = true;
    }
  }

  const pointerAtBeginning = hasStartArrow; // Show arrow at beginning if hasStartArrow is true
  const pointerAtEnding = hasEndArrow; // Show arrow at end if hasEndArrow is true

  const shapeRef = useRef();
  const groupRef = useRef();
  const startDotRef = useRef();
  const endDotRef = useRef();
  const isDraggingRef = useRef(false);
  const isDraggingPointRef = useRef(false);
  const suppressPointDragUntilRef = useRef(0);
  const bendRemoveCooldownUntilRef = useRef(0);

  const stopAllControlPointDrags = () => {
    isDraggingPointRef.current = false;
    try {
      const active = Konva.DD?.node;
      if (active && typeof active.stopDrag === "function") {
        active.stopDrag();
      }
    } catch (_) {
      /* ignore */
    }
    try {
      const circles = groupRef.current?.find?.("Circle");
      circles?.forEach((c) => {
        try {
          c.stopDrag();
        } catch (_) {
          /* ignore */
        }
      });
    } catch (_) {
      /* ignore */
    }
  };
  const groupDragPointerStartRef = useRef(null); // pointer position at drag start for group-drag delta
  const lastPointerRef = useRef({ clientX: undefined, clientY: undefined }); // last pointer position during drag (fallback when drag end evt has no coords)
  const lastSelectUpdateRef = useRef(0);
  const storedWidthHeightRef = useRef({
    width: element.width,
    height: element.height,
  });

  // CRITICAL FIX: Removed lastDraggedPositionRef logic entirely
  // This ref was causing premature drag end when crossing canvas boundaries
  // CircleElement doesn't use this pattern and works correctly
  // Let Konva handle drag state naturally without ref interference

  // CRITICAL FIX: During group drag, prevent React from overriding Konva node position
  // handleGroupDragMove updates the Konva node directly, and we need to preserve that
  useEffect(() => {
    if (!groupRef.current) return;

    const node = groupRef.current;
    const isInGroupDrag = window.isGroupDraggingRef?.current === true;

    // Only maintain position during active group drag
    if (isInGroupDrag) {
      // Store the current node position (which was set by handleGroupDragMove)
      const currentNodeX = node.x();
      const currentNodeY = node.y();

      // If React-Konva tries to apply props that differ from the node position,
      // restore the node position (which is the correct one from handleGroupDragMove)
      if (
        Math.abs(currentNodeX - (element.x || 0)) > 0.1 ||
        Math.abs(currentNodeY - (element.y || 0)) > 0.1
      ) {
        // Node position differs from props - keep the node position (it's more current)
        node.x(currentNodeX);
        node.y(currentNodeY);
      }
    }
  }, [element.x, element.y]);

  const [points, setPoints] = useState(() => {
    const raw = element.points || [0, 0, 0, 0];
    const hasArrow =
      element.hasStartArrow === true || element.hasEndArrow === true;
    return hasArrow ? ensureNonZeroLengthPointsForArrow(raw, element) : raw;
  });

  const currentPointsRef = useRef(points);
  const [currentPoints, setCurrentPoints] = useState(points);

  useEffect(() => {
    if (isDraggingRef.current || isDraggingPointRef.current) {
      return;
    }
    let newRelativePoints = element.points || [0, 0, 0, 0];
    if (pointerAtBeginning || pointerAtEnding) {
      newRelativePoints = ensureNonZeroLengthPointsForArrow(
        newRelativePoints,
        element,
      );
    }
    currentPointsRef.current = newRelativePoints;
    setCurrentPoints(newRelativePoints);
  }, [
    element.points,
    element.x,
    element.y,
    pointerAtBeginning,
    pointerAtEnding,
    element.width,
    // Removed hasStartArrow and hasEndArrow - these shouldn't trigger point updates
  ]);

  // Force re-render when arrow flags change to ensure correct display
  // When switching between Arrow and Line, we need to ensure the layer redraws
  useEffect(() => {
    // Trigger a layer redraw whenever arrow properties change
    // This ensures smooth transitions between Arrow and Line components
    const layer = groupRef.current?.getLayer();
    if (layer) {
      layer.batchDraw();
    }
  }, [
    element.hasStartArrow,
    element.hasEndArrow,
    element.id,
    pointerAtBeginning,
    pointerAtEnding,
  ]);

  useEffect(() => {
    const isInGroupDrag = window.isGroupDraggingRef?.current === true;
    if (isDraggingRef.current || isInGroupDrag) {
      return;
    }
    setTimeout(() => {
      const isStillInGroupDrag = window.isGroupDraggingRef?.current === true;
      // CRITICAL: Do NOT sync position during cross-canvas drag (like CircleElement).
      // Overwriting the node with element.x/y would fight Konva's drag and cause vibration.
      const isCrossCanvasDrag =
        element._useNodePositionForGroup === true ||
        element._isInOffCanvasDuringDrag === true;
      if (
        groupRef.current &&
        !isDraggingRef.current &&
        !isStillInGroupDrag &&
        !isCrossCanvasDrag
      ) {
        const newX = element.x || 0;
        const newY = element.y || 0;
        const currentX = groupRef.current.x();
        const currentY = groupRef.current.y();

        if (newX !== currentX || newY !== currentY) {
          groupRef.current.x(newX);
          groupRef.current.y(newY);
          groupRef.current.getLayer()?.batchDraw();
        }
      }
    }, 50);
  }, [
    element.x,
    element.y,
    element.id,
    element._useNodePositionForGroup,
    element._isInOffCanvasDuringDrag,
    isOffCanvas,
  ]);

  // CRITICAL FIX: Removed useLayoutEffect position syncing entirely
  // This was interfering with drag operations, especially cross-canvas drags
  // CircleElement doesn't have this and works correctly
  // Let Konva handle position updates naturally through props

  function clampAndNormalize(x0, y0, x1, y1) {
    let dx = x1 - x0;
    let dy = y1 - y0;
    let width = Math.abs(dx);
    let height = Math.abs(dy);
    let signX = dx >= 0 ? 1 : -1;
    let signY = dy >= 0 ? 1 : -1;
    let clampedWidth = Math.max(MIN_WIDTH, Math.min(maxLineLayoutWidth, width));
    let clampedHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height));
    let newX1 = x0 + signX * clampedWidth;
    let newY1 = y0 + signY * clampedHeight;
    let minX = Math.min(x0, newX1);
    let minY = Math.min(y0, newY1);
    let points = [x0 - minX, y0 - minY, newX1 - minX, newY1 - minY];
    return {
      points,
      groupX: minX,
      groupY: minY,
      absPoints: [x0, y0, newX1, newY1],
    };
  }

  const updatePoint = (index, newPointLocalX, newPointLocalY, e) => {
    const currentRelativePoints = [...currentPointsRef.current];

    // Simply update the dragged point's position without scaling other points
    const updatedPoints = [...currentRelativePoints];
    updatedPoints[index] = newPointLocalX;
    updatedPoints[index + 1] = newPointLocalY;

    const groupX = groupRef.current?.x() ?? element.x ?? 0;
    const groupY = groupRef.current?.y() ?? element.y ?? 0;

    let constrainedPoints = [...updatedPoints];

    if (!isOffCanvas && canvasWidth && canvasHeight) {
      const canvasPadding = MAIN_STAGE_EDGE_BUFFER_PX;
      const absPointX = newPointLocalX + groupX;
      const absPointY = newPointLocalY + groupY;

      const constrainedAbsX = Math.max(
        canvasPadding,
        Math.min(canvasWidth - canvasPadding, absPointX),
      );
      const constrainedAbsY = Math.max(
        canvasPadding,
        Math.min(canvasHeight - canvasPadding, absPointY),
      );

      constrainedPoints[index] = constrainedAbsX - groupX;
      constrainedPoints[index + 1] = constrainedAbsY - groupY;
    }

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let i = 0; i < constrainedPoints.length; i += 2) {
      minX = Math.min(minX, constrainedPoints[i]);
      maxX = Math.max(maxX, constrainedPoints[i]);
      minY = Math.min(minY, constrainedPoints[i + 1]);
      maxY = Math.max(maxY, constrainedPoints[i + 1]);
    }

    let width = maxX - minX;
    let height = maxY - minY;

    const maxDisplayWidth = Math.max(
      MIN_WIDTH,
      (Number(canvasWidth) || getMainCanvasWidthPx()) -
        MAIN_STAGE_EDGE_BUFFER_PX * 2,
    );
    const isStretchingFromEnd = index >= constrainedPoints.length - 2;
    const isStretchingFromStart = index <= 1;

    if (
      width > maxDisplayWidth &&
      !isStretchingFromEnd &&
      !isStretchingFromStart &&
      !isOffCanvas
    ) {
      const centerX = (minX + maxX) / 2;
      const scale = maxDisplayWidth / (width || 1);
      for (let i = 0; i < constrainedPoints.length; i += 2) {
        const relativeToCenter = constrainedPoints[i] - centerX;
        constrainedPoints[i] = centerX + relativeToCenter * scale;
      }
      width = maxDisplayWidth;
      minY = Infinity;
      maxY = -Infinity;
      for (let i = 1; i < constrainedPoints.length; i += 2) {
        minY = Math.min(minY, constrainedPoints[i]);
        maxY = Math.max(maxY, constrainedPoints[i]);
      }
      height = maxY - minY;
    }

    currentPointsRef.current = constrainedPoints;
    setCurrentPoints(constrainedPoints);

    if (shapeRef.current) {
      shapeRef.current.points(constrainedPoints);
    }

    // Update all circle positions after scaling
    // Find all Circle nodes in the group and update their positions
    // Skip updating the currently dragged circle as Konva handles its position during drag
    if (groupRef.current && !isDraggingPointRef.current) {
      const circles = groupRef.current.find("Circle");
      circles.forEach((circle, circleIndex) => {
        const pointIndex = circleIndex * 2;
        if (pointIndex < constrainedPoints.length - 1) {
          circle.x(constrainedPoints[pointIndex]);
          circle.y(constrainedPoints[pointIndex + 1]);
        }
      });
      groupRef.current.getLayer()?.batchDraw();
    } else if (groupRef.current && isDraggingPointRef.current) {
      // During drag, update all circles except the one being dragged
      const circles = groupRef.current.find("Circle");
      circles.forEach((circle, circleIndex) => {
        const pointIndex = circleIndex * 2;
        // Skip the circle being dragged (it's at index/2)
        if (pointIndex !== index && pointIndex < constrainedPoints.length - 1) {
          circle.x(constrainedPoints[pointIndex]);
          circle.y(constrainedPoints[pointIndex + 1]);
        }
      });
      groupRef.current.getLayer()?.batchDraw();
    }

    const absPoints = constrainedPoints.map((coord, i) =>
      i % 2 === 0 ? coord + groupX : coord + groupY,
    );

    // CRITICAL FIX: Add throttling to onUpdate callback to match imageRenderer behavior
    // This prevents fluctuation when images are present in the canvas
    const now = Date.now();
    const shouldUpdate =
      now - _lastUnifiedElementDragStateTime >= DRAG_STATE_THROTTLE_MS;

    if (onUpdate && shouldUpdate) {
      _lastUnifiedElementDragStateTime = now;
      onUpdate(
        element.id,
        absPoints,
        (constrainedPoints.length - 4) / 2,
        element.angle,
        groupX,
        groupY,
        width,
        height,
        e,
      );
    }

    if (isSelected && onSelect) {
      const now = Date.now();
      const throttleDelay = 16;

      if (now - lastSelectUpdateRef.current > throttleDelay) {
        const elementType =
          pointerAtBeginning && pointerAtEnding
            ? "DoubleSidedArrow"
            : pointerAtBeginning || pointerAtEnding
              ? "Arrow"
              : "Line";

        const updatedElement = {
          id: element.id,
          type: elementType,
          points: constrainedPoints, // Use relative points, not absolute
          strokeColor:
            element.stroke || element.strokeColor || strokeColor || "#000000",
          stroke:
            element.stroke || element.strokeColor || strokeColor || "#000000",
          strokeWidth: element.strokeWidth || 2,
          isDashed: element.isDashed || false,
          x: groupX,
          y: groupY,
          width,
          height,
          rotation: element.rotation || 0,
          hasStartArrow: element.hasStartArrow,
          hasEndArrow: element.hasEndArrow,
          name: element.name,
        };

        const syntheticEvent = {
          target: { attrs: { type: elementType, id: element.id } },
          evt: { shiftKey: false },
        };

        onSelect(syntheticEvent, updatedElement);
        lastSelectUpdateRef.current = now;
      }
    }
  };

  const handleDragMove = (e) => {
    // CRITICAL SAFETY CHECK: Only allow drag if mouse button is actually pressed
    if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
      const node = e.target;
      if (node) {
        node.stopDrag();
      }
      isDraggingRef.current = false;
      setIsMultiSelectDragActive(false);
      // CRITICAL FIX: Clear global dragging flag when drag is stopped
      window.isUnifiedLineArrowDragging = false;
      return;
    }

    const node = e.target;
    if (!node) return;
    let newX = node.x();
    let newY = node.y();

    isDraggingRef.current = true;

    // CRITICAL FIX: Set global flag so top-layer stage can detect this drag
    // This prevents interference from top-layer images when they are present
    window.isUnifiedLineArrowDragging = true;

    // Store pointer position for drag end (fallback when evt has no clientX/Y e.g. touchend)
    const ptr =
      e?.evt?.clientX != null
        ? { clientX: e.evt.clientX, clientY: e.evt.clientY }
        : e?.evt?.touches?.[0]
          ? {
              clientX: e.evt.touches[0].clientX,
              clientY: e.evt.touches[0].clientY,
            }
          : e?.evt?.changedTouches?.[0]
            ? {
                clientX: e.evt.changedTouches[0].clientX,
                clientY: e.evt.changedTouches[0].clientY,
              }
            : null;
    if (ptr) lastPointerRef.current = ptr;

    // CRITICAL FIX: Removed complex boundary detection and coordinate conversion
    // This was causing premature dragEnd when crossing canvas boundaries
    // CircleElement and RectangleElement don't do this - they let Konva handle drag naturally
    // Canvas.jsx's handleElementDragEnd handles cross-canvas transfer after drag completes

    // Calculate bounding box from points (needed for both constraint and callback)
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    const pts = currentPointsRef.current;
    for (let i = 0; i < pts.length; i += 2) {
      // Use stage coords for bbox; this node is still on the source stage during drag.
      const x = pts[i] + newX;
      const y = pts[i + 1] + newY;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    const width = maxX - minX;
    const height = maxY - minY;

    if (node) {
      // Do not clamp during dragMove; allow crossing stage boundaries while dragging.
      // Final position constraints/transfer are handled in dragEnd pipeline from Canvas.jsx.

      // Recalculate bounding box with live drag position.
      minX = Infinity;
      minY = Infinity;
      maxX = -Infinity;
      maxY = -Infinity;
      for (let i = 0; i < pts.length; i += 2) {
        const x = pts[i] + newX;
        const y = pts[i + 1] + newY;
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    }

    const finalWidth = maxX - minX;
    const finalHeight = maxY - minY;

    // When in group drag, dragBoundFunc keeps the Group from moving so node.x/y never change.
    // Pass initialPos + (pointerNow - pointerStart) so handleGroupDragMove gets the correct delta.
    let callbackX = newX;
    let callbackY = newY;
    if (isInMultiSelectionForDrag && groupDragPointerStartRef.current) {
      const stage = node.getStage();
      if (stage) {
        const pos = stage.getPointerPosition();
        if (pos) {
          const start = groupDragPointerStartRef.current;
          const initialX = element.x ?? 0;
          const initialY = element.y ?? 0;
          callbackX = initialX + (pos.x - start.x);
          callbackY = initialY + (pos.y - start.y);
        }
      }
    }

    // Store pointer position for drag-end fallback (when evt has no clientX/clientY, e.g. touchend)
    const px =
      e?.evt?.clientX ??
      e?.evt?.touches?.[0]?.clientX ??
      e?.evt?.changedTouches?.[0]?.clientX;
    const py =
      e?.evt?.clientY ??
      e?.evt?.touches?.[0]?.clientY ??
      e?.evt?.changedTouches?.[0]?.clientY;
    if (px !== undefined && py !== undefined) {
      lastPointerRef.current = { clientX: px, clientY: py };
    }

    // CRITICAL FIX: Add throttling to match imageRenderer behavior
    // This prevents fluctuation when images are present in the canvas
    const now = Date.now();
    const shouldUpdate =
      now - _lastUnifiedElementDragStateTime >= DRAG_STATE_THROTTLE_MS;

    if (shouldUpdate) {
      _lastUnifiedElementDragStateTime = now;

      // CRITICAL FIX: Pass simple coordinates without conversion
      // Let Canvas.jsx's handleElementDragEnd handle cross-canvas transfer logic
      // This matches how CircleElement and RectangleElement work
      onDragMove?.(
        {
          ...element,
          id: element.id,
          x: callbackX,
          y: callbackY,
          points: currentPointsRef.current,
          width: finalWidth || element.width || 0,
          height: finalHeight || element.height || 0,
          rotation: element.rotation || 0,
        },
        e,
      );
    }
  };

  const handleDragEnd = (e) => {
    const node = groupRef.current;
    if (!node) return;

    setIsMultiSelectDragActive(false);

    // Clear cross-canvas locked position so next drag can lock again
    crossCanvasLockedPositionRef.current = null;
    offCanvasDragStripSideRef.current = null;

    // CRITICAL SAFETY CHECK: Verify mouse button is actually released
    // If button is still pressed, this is a false dragEnd triggered by re-render
    if (e?.evt?.buttons !== undefined && e.evt.buttons !== 0) {
      return;
    }

    const newX = node.x();
    const newY = node.y();

    // Check if element is in multi-selection
    const isInMultiSelection =
      selectedObjectsArray &&
      selectedObjectsArray.length > 1 &&
      selectedObjectsArray.some((obj) => obj.id === element.id);


    // CRITICAL FIX: Don't store dragged position in ref - let Konva handle it naturally
    // This matches CircleElement behavior and prevents premature drag end when crossing canvases
    // The ref was causing useEffect hooks to interfere with ongoing drags

    // CRITICAL: Always call onDragEnd, even during multi-selection
    // Pass mouse position so handleElementDragEnd can detect drop on the other stage and transfer (prevents element vanishing)
    // Use changedTouches for touch end (touches is empty on touchend); fallback to last stored pointer when evt has no coords
    const mouseClientX =
      e?.evt?.clientX ??
      e?.evt?.touches?.[0]?.clientX ??
      e?.evt?.changedTouches?.[0]?.clientX ??
      lastPointerRef.current?.clientX;
    const mouseClientY =
      e?.evt?.clientY ??
      e?.evt?.touches?.[0]?.clientY ??
      e?.evt?.changedTouches?.[0]?.clientY ??
      lastPointerRef.current?.clientY;
    onDragEnd(
      element.id,
      newX,
      newY,
      sourceStageRef,
      mouseClientX,
      mouseClientY,
    );
    if (saveToHistory) saveToHistory();

    // Delay resetting dragging flag to allow state update to complete
    setTimeout(() => {
      isDraggingRef.current = false;
      // CRITICAL FIX: Clear global dragging flag when drag ends
      window.isUnifiedLineArrowDragging = false;
    }, 50);
  };

  const removeBendPoint = (index, e) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation?.();
    e.evt?.preventDefault?.();
    e.evt?.stopImmediatePropagation?.();

    if (Date.now() < bendRemoveCooldownUntilRef.current) {
      return;
    }

    const existingPoints = currentPointsRef.current || [];
    const n = existingPoints.length;
    if (n <= 4) return;

    let spliceAt = -1;
    if (index > 1 && index < n - 2) {
      spliceAt = index;
    } else {
      return;
    }

    const updatedPoints = [...existingPoints];
    updatedPoints.splice(spliceAt, 2);

    const gestureEnd = Date.now() + 450;
    bendRemoveCooldownUntilRef.current = gestureEnd;
    suppressPointDragUntilRef.current = gestureEnd;
    stopAllControlPointDrags();

    currentPointsRef.current = updatedPoints;
    setCurrentPoints(updatedPoints);

    if (shapeRef.current) {
      shapeRef.current.points(updatedPoints);
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < updatedPoints.length; i += 2) {
      minX = Math.min(minX, updatedPoints[i]);
      maxX = Math.max(maxX, updatedPoints[i]);
      minY = Math.min(minY, updatedPoints[i + 1]);
      maxY = Math.max(maxY, updatedPoints[i + 1]);
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const groupX = groupRef.current?.x() ?? element.x ?? 0;
    const groupY = groupRef.current?.y() ?? element.y ?? 0;
    const absPoints = updatedPoints.map((coord, i) =>
      i % 2 === 0 ? coord + groupX : coord + groupY,
    );

    onUpdate?.(
      element.id,
      absPoints,
      (updatedPoints.length - 4) / 2,
      element.angle,
      groupX,
      groupY,
      width,
      height,
      e,
    );

    if (isSelected && onSelect) {
      const elementType =
        pointerAtBeginning && pointerAtEnding
          ? "DoubleSidedArrow"
          : pointerAtBeginning || pointerAtEnding
            ? "Arrow"
            : "Line";

      const updatedElement = {
        id: element.id,
        type: elementType,
        points: updatedPoints,
        strokeColor:
          element.stroke || element.strokeColor || strokeColor || "#000000",
        stroke:
          element.stroke || element.strokeColor || strokeColor || "#000000",
        strokeWidth: element.strokeWidth || 2,
        isDashed: element.isDashed || false,
        x: groupX,
        y: groupY,
        width,
        height,
        rotation: element.rotation || 0,
        hasStartArrow: element.hasStartArrow,
        hasEndArrow: element.hasEndArrow,
        name: element.name,
      };

      const syntheticEvent = {
        target: { attrs: { type: elementType, id: element.id } },
        evt: { shiftKey: false },
      };
      onSelect(syntheticEvent, updatedElement);
    }

    groupRef.current?.getLayer()?.batchDraw();
    saveToHistory?.();

    requestAnimationFrame(() => {
      stopAllControlPointDrags();
      groupRef.current?.getLayer()?.batchDraw();
    });
  };

  const handleElementSelect = (e) => {
    if (!onSelect) {
      return;
    }
    if (!e) {
      return;
    }

    // Disable selection if a tool is locked or clone mode is active (same as rectangle behavior)
    if (lockedAction || isCloneMode) {
      return;
    }

    // Determine element type based on arrow flags
    const isDoubleSidedArrow = hasStartArrow && hasEndArrow;
    const isSingleSidedArrow =
      (hasStartArrow && !hasEndArrow) || (!hasStartArrow && hasEndArrow);

    const elementType = isDoubleSidedArrow
      ? "DoubleSidedArrow"
      : isSingleSidedArrow
        ? "Arrow"
        : "Line";

    const existingPoints = currentPointsRef.current;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < existingPoints.length; i += 2) {
      minX = Math.min(minX, existingPoints[i]);
      maxX = Math.max(maxX, existingPoints[i]);
      minY = Math.min(minY, existingPoints[i + 1]);
      maxY = Math.max(maxY, existingPoints[i + 1]);
    }

    const width = maxX - minX;
    const height = maxY - minY;

    const x = element.x || 0;
    const y = element.y || 0;
    const rotation = element.rotation || 0;

    const normalizeStrokeColor = (color) => {
      if (!color) return "#000000";
      if (typeof color === "string" && color.startsWith("#")) return color;
      if (typeof color === "string") return color;
      if (typeof color === "number") {
        return `#${color.toString(16).padStart(6, "0")}`;
      }
      return "#000000";
    };

    const rawStrokeColor =
      element.stroke || element.strokeColor || strokeColor || "#000000";
    const normalizedStrokeColor = normalizeStrokeColor(rawStrokeColor);

    // CRITICAL: Use existingPoints (current relative points) instead of points (stale state)
    // Keep points as relative coordinates - don't convert to absolute
    // This prevents position jumps when re-selecting elements with bends
    const selectedElement = {
      id: element.id,
      type: elementType,
      strokeWidth: element.strokeWidth || 2,
      isDashed: element.isDashed || false,
      bend: element.bend || 0,
      angle: element.angle || 90,
      points: [...existingPoints], // Use current relative points, keep them relative
      fillColor: element.fillColor || "#000000",
      strokeColor: normalizedStrokeColor,
      stroke: normalizedStrokeColor,
      x,
      y,
      width,
      height,
      rotation,
      hasStartArrow: element.hasStartArrow,
      hasEndArrow: element.hasEndArrow,
      name: element.name,
    };

    // CRITICAL: Stop event propagation BEFORE calling onSelect to prevent canvas click handler from running
    if (e) {
      e.cancelBubble = true;
      e.evt?.stopPropagation?.();
      e.evt?.preventDefault?.();
      e.evt?.stopImmediatePropagation?.();
    }

    onSelect(e, selectedElement);
  };

  // Konva drags update node positions imperatively; React doesn't re-render on every drag tick.
  // Use state so we can reliably hide the blue control orbs during multi-selection drags.
  const [isMultiSelectDragActive, setIsMultiSelectDragActive] = useState(false);

  // CRITICAL: Match CircleElement - use element.x / element.y for Group position (no cross-canvas lock).
  if (!element || !element.id || !currentPoints || currentPoints.length < 4) {
    return null;
  }

  const groupX = element.x ?? 0;
  const groupY = element.y ?? 0;
  const stroke =
    element.stroke || element.strokeColor || strokeColor || "#000000";
  const strokeWidth = element.strokeWidth || 2;
  const isDashed = element.isDashed || false;
  // Keep arrowheads proportional, but stop growth at heavy strokes.
  // This avoids oversized heads once strokeWidth crosses ~12.
  const cappedStrokeForPointer = Math.min(strokeWidth, 10);
  const pointerLength = Math.max(10, cappedStrokeForPointer * 2.6);
  const pointerWidth = Math.max(8, cappedStrokeForPointer * 1.8);

  // CRITICAL: Include arrow flags in the key to force re-render when they change
  // This ensures React treats dragging vs not-dragging as the same component instance
  // DO NOT include drag state in key as it causes remounts that interrupt drag
  const groupKey = `unified-line-arrow-${element.id}-${arrowFlags.hasStartArrow}-${arrowFlags.hasEndArrow}`;

  // CRITICAL FIX: Simplified ref callback - just store the ref
  // Position is now handled by passing element.x/y directly to Group props (like RectangleElement)
  // No need to manually sync positions in the callback
  const groupRefCallback = (node) => {
    if (node) {
      groupRef.current = node;
    }
  };

  const normalizedSelectedIds = Array.from(
    new Set(
      (selectedObjectsArray || [])
        .map((obj) =>
          typeof obj?.id === "string" ? obj.id.replace(/-ghost$/, "") : obj?.id,
        )
        .filter(Boolean),
    ),
  );
  const selectedUniqueCount = normalizedSelectedIds.length;

  // When in multi-select, lock Group position so only handleGroupDragMove moves elements (no "little move" before group drag)
  const isInMultiSelectionForDrag =
    selectedUniqueCount > 1 &&
    normalizedSelectedIds.includes(
      typeof element?.id === "string"
        ? element.id.replace(/-ghost$/, "")
        : element?.id,
    );

  // CRITICAL: Match CircleElement - only lock for multi-selection drag. No cross-canvas lock (was causing stick at boundary).
  const dragBoundFunc = isInMultiSelectionForDrag
    ? (pos) => {
        const n = groupRef.current;
        return n ? { x: n.x(), y: n.y() } : pos;
      }
    : undefined;

  // Decide when blue control point circles should be visible.
  // They should be shown whenever:
  // - the element is selected and blue circles are not explicitly hidden, OR
  // - the element (or any of its control points) is currently being dragged.
  // Use both internal drag refs and Konva node's isDragging() for robustness across drag transitions.
  const isNodeDragging =
    !!groupRef.current && typeof groupRef.current.isDragging === "function"
      ? groupRef.current.isDragging()
      : false;

  // Treat any active drag (group or control point) as a strong signal to keep blue orbs visible.
  const isActivelyDragging =
    isDraggingRef.current || isDraggingPointRef.current || isNodeDragging;

  // Detect cross‑canvas preview copies of this element:
  // during main→off or off→main drag, the non‑dragging copy is marked with preview flags
  // (_isInOffCanvasDuringDrag / _previewTargetStage) but isn't itself "dragging".
  // We still want blue orbs visible on that preview copy.
  const isCrossCanvasPreviewLike =
    isPreview ||
    element?._isInOffCanvasDuringDrag === true ||
    element?._previewTargetStage === "offCanvas" ||
    element?._previewTargetStage === "main";

  const elementIdForSelectionCheck =
    typeof element?.id === "string" ? element.id.replace(/-ghost$/, "") : element?.id;

  // If this element is already part of the selectedObjectsArray (multi-select),
  // don't show blue control points on the off-canvas drag preview/ghost copy.
  const isInSelectedObjectsArray =
    selectedUniqueCount > 1 &&
    normalizedSelectedIds.includes(elementIdForSelectionCheck);

  const shouldHideBlueCirclesForGroupPreview = isPreview && isInSelectedObjectsArray;

  // Requested behavior:
  // When dragging a unified line/arrow that is part of a multi-selection (>= 2 elements),
  // do not show blue orbs.
  const shouldHideBlueCirclesForMultiSelectionDrag =
    isMultiSelectDragActive && isInSelectedObjectsArray;

  const showBlueCircles =
    shouldHideBlueCirclesForMultiSelectionDrag
      ? false
      : shouldHideBlueCirclesForGroupPreview
        ? false
        :
    // Explicit force from renderer (used for ghosts/previews)
    (forceShowBlueCircles && !isInSelectedObjectsArray) ||
    // Always show on any preview‑like copy, regardless of drag refs
    (isCrossCanvasPreviewLike &&
      !forceHideBlueCircles &&
      !isInSelectedObjectsArray) ||
    // Show while actively dragging, unless explicitly forced hidden
    (isActivelyDragging && !forceHideBlueCircles && !hideBlueCircles) ||
    // Fallback: normal selection logic
    (!forceHideBlueCircles &&
      ((isSelected && !hideBlueCircles) ||
        (isActivelyDragging && !hideBlueCircles)));

  return (
    <Group
      ref={groupRefCallback}
      id={element.id}
      name={element.name}
      key={groupKey}
      x={groupX}
      y={groupY}
      scaleX={1}
      scaleY={1}
      rotation={element.rotation || 0}
      dragDistance={isInMultiSelectionForDrag ? 0 : 10}
      draggable={isDraggable}
      perfectDrawEnabled={false}
      shadowForStrokeEnabled={false}
      opacity={element.opacity !== undefined ? element.opacity : 1}
      visible={element.opacity === 0 ? false : true}
      dragBoundFunc={dragBoundFunc}
      onDragStart={(e) => {
        // CRITICAL SAFETY CHECK: Verify mouse button is actually pressed
        if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
          const node = e.target;
          if (node) {
            node.stopDrag();
          }
          isDraggingRef.current = false;
          offCanvasDragStripSideRef.current = null;
          return;
        }

        isDraggingRef.current = true;
        e.target.setAttr("dragDistance", 0);
        // Store pointer position at drag start so handleDragMove can compute delta for group drag
        if (isInMultiSelectionForDrag) {
          setIsMultiSelectDragActive(true);
          // Hide existing circles immediately (React-Konva will unmount them soon
          // after the state change, but this avoids any visible flicker).
          try {
            const circles = groupRef.current?.find("Circle") || [];
            circles.forEach((c) => c.visible(false));
            groupRef.current?.getLayer?.()?.batchDraw?.();
          } catch (err) {
            // ignore - visibility will be corrected by React on re-render
          }
          const stage = e.target.getStage();
          if (stage) {
            const pos = stage.getPointerPosition();
            groupDragPointerStartRef.current = pos
              ? { x: pos.x, y: pos.y }
              : null;
          } else {
            groupDragPointerStartRef.current = null;
          }
        } else {
          groupDragPointerStartRef.current = null;
          setIsMultiSelectDragActive(false);
        }
        if (isOffCanvas && e.target) {
          const st = e.target.getStage?.();
          const sw = st?.width?.() ?? 0;
          if (sw > 0) {
            const pts = currentPointsRef.current || element.points || [];
            const et =
              element.type ||
              (pointerAtBeginning && pointerAtEnding
                ? "DoubleSidedArrow"
                : pointerAtBeginning || pointerAtEnding
                  ? "Arrow"
                  : "Line");
            const nx = e.target.x();
            const ext = getAbsoluteHorizontalExtent(
              { ...element, points: pts, x: nx, y: e.target.y() },
              et,
              nx,
            );
            offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
              ext.minX,
              ext.maxX,
              sw,
            );
          } else {
            offCanvasDragStripSideRef.current = null;
          }
        } else {
          offCanvasDragStripSideRef.current = null;
        }
        // Trigger group drag instantly when in multi-select (parent calls handleGroupDragStart)
        onDragStart?.(element, e);
      }}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onClick={handleElementSelect}
    >
      {/* Use Line component when both checkboxes are unchecked, Arrow component when at least one is checked */}
      {/* Use key to force remount when switching between Arrow and Line */}
      {pointerAtBeginning || pointerAtEnding ? (
        <Arrow
          key={`arrow-${element.id}-${pointerAtBeginning}-${pointerAtEnding}`}
          ref={(ref) => {
            shapeRef.current = ref;
            if (ref) {
              // Ensure the stroke path is rendered beneath control-point circles
              // within this group and layer to avoid visually covering blue orbs,
              // including during cross‑canvas previews.
              try {
                ref.moveToBottom();
                const layer = ref.getLayer();
                layer && layer.batchDraw();
              } catch (e) {
                // graceful fallback
              }

              // Set type attribute on the Konva node when it's created
              const elementType =
                element.type ||
                (pointerAtBeginning && pointerAtEnding
                  ? "DoubleSidedArrow"
                  : pointerAtBeginning || pointerAtEnding
                    ? "Arrow"
                    : "Line");
              ref.attrs.type = elementType;
              ref.attrs.id = element.id;
              ref.attrs.name = element.name;
            }
          }}
          id={element.id}
          name={element.name}
          points={currentPoints}
          length={element.length}
          stroke={stroke}
          strokeWidth={strokeWidth}
          hitStrokeWidth={Math.max(10, strokeWidth + 8)}
          fill={stroke}
          pointerAtBeginning={pointerAtBeginning}
          pointerAtEnding={pointerAtEnding}
          pointerLength={pointerLength}
          pointerWidth={pointerWidth}
          rotation={element.rotation || 0}
          draggable={false}
          perfectDrawEnabled={false}
          listening={true}
          shadowForStrokeEnabled={false}
          lineCap="round"
          lineJoin="miter"
          dash={isDashed ? [10, 5] : []}
          className="konva-arrow"
          onClick={(e) => {
            if (!isPrimaryMouseButton(e.evt)) {
              return;
            }
            // CRITICAL: Stop propagation immediately to prevent canvas click handler from running
            e.cancelBubble = true;
            e.evt?.stopPropagation?.();
            e.evt?.preventDefault?.();
            e.evt?.stopImmediatePropagation?.();
            // Determine type for event
            const elementType =
              element.type ||
              (pointerAtBeginning && pointerAtEnding
                ? "DoubleSidedArrow"
                : pointerAtBeginning || pointerAtEnding
                  ? "Arrow"
                  : "Line");
            e.target.attrs.type = elementType;
            e.target.attrs.id = element.id;
            e.target.attrs.name = element.name;
            // Call handleElementSelect which will call onSelect
            handleElementSelect(e);
          }}
        />
      ) : (
        <Line
          key={`line-${element.id}-${pointerAtBeginning}-${pointerAtEnding}`}
          ref={(ref) => {
            shapeRef.current = ref;
            if (ref) {
              // Ensure the stroke path is rendered beneath control-point circles
              // within this group and layer to avoid visually covering blue orbs.
              try {
                ref.moveToBottom();
                const layer = ref.getLayer();
                layer && layer.batchDraw();
              } catch (e) {
                // graceful fallback
              }

              // Set type attribute on the Konva node when it's created
              ref.attrs.type = element.type || "Line";
              ref.attrs.id = element.id;
              ref.attrs.name = element.name;
            }
          }}
          id={element.id}
          name={element.name}
          points={currentPoints}
          stroke={stroke}
          strokeWidth={strokeWidth}
          hitStrokeWidth={Math.max(10, strokeWidth + 8)}
          listening={true}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
          lineCap="round"
          lineJoin="round"
          dash={isDashed ? [10, 5] : []}
          tension={0}
          onClick={(e) => {
            if (!isPrimaryMouseButton(e.evt)) {
              return;
            }
            // CRITICAL: Stop propagation immediately to prevent canvas click handler from running
            e.cancelBubble = true;
            e.evt?.stopPropagation?.();
            e.evt?.preventDefault?.();
            e.evt?.stopImmediatePropagation?.();
            e.target.attrs.type = element.type || "Line";
            e.target.attrs.id = element.id;
            e.target.attrs.name = element.name;
            // Call handleElementSelect which will call onSelect
            handleElementSelect(e);
          }}
        />
      )}

      {/* Blue control point circles */}
      {/* Show blue orbs when:
          1. Element is selected in normal mode (isSelected && !isMultiSelectMode)
          2. OR in multi-select mode, element is the only one selected (isMultiSelectMode && selectedObjectsArray.length === 1)
          hideBlueCircles is now controlled by the renderer to be true only when multiple elements are selected
      */}
      {showBlueCircles &&
        currentPoints.map((_, index) =>
          index % 2 === 0 ? (
            <Circle
              key={index}
              ref={(node) => {
                if (!node) return;
                // Defer z-order adjustment until after Konva has mounted all siblings (Arrow/Line + Circles)
                // so that control points reliably sit visually on top, including in cross‑canvas previews.
                window.requestAnimationFrame(() => {
                  if (!node.getLayer || !node.getLayer()) return;
                  node.moveToTop();
                  const layer = node.getLayer();
                  layer && layer.batchDraw();
                });
              }}
              x={currentPoints[index]}
              y={currentPoints[index + 1]}
              radius={6}
              fill="blue"
              name={element.name}
              stroke="white"
              strokeWidth={Math.max(
                1.5,
                1.5 / Math.max(0.3, effectiveZoomLevel || 1),
              )}
              perfectDrawEnabled={false}
              shadowForStrokeEnabled={false}
              draggable
              dragDistance={(() => {
                const len = currentPoints.length;
                const isEndpoint = index === 0 || index === len - 2;
                return len > 4 && isEndpoint ? 28 : 12;
              })()}
              onMouseDown={(e) => {
                const pts = currentPointsRef.current || [];
                const len = pts.length;
                const isInteriorBend =
                  len > 4 && index > 1 && index < len - 2;
                const canDblRemoveBend = len > 4 && isInteriorBend;
                // Second click of a double-click: remove bend and never start a drag
                if (canDblRemoveBend && e.evt?.detail === 2) {
                  e.cancelBubble = true;
                  e.evt?.preventDefault?.();
                  e.evt?.stopPropagation?.();
                  try {
                    e.target?.stopDrag?.();
                  } catch (_) {
                    /* ignore */
                  }
                  stopAllControlPointDrags();
                  removeBendPoint(index, e);
                }
              }}
              onDragStart={(e) => {
                if (Date.now() < suppressPointDragUntilRef.current) {
                  e.cancelBubble = true;
                  e.target?.stopDrag?.();
                  return;
                }
                const pts = currentPointsRef.current || [];
                const len = pts.length;
                const isStartOrEnd = index === 0 || index === len - 2;
                // Konva can still fire dragstart on the 2nd mousedown of a double-click
                if (
                  isStartOrEnd &&
                  len > 4 &&
                  e.evt &&
                  typeof e.evt.detail === "number" &&
                  e.evt.detail >= 2
                ) {
                  e.cancelBubble = true;
                  try {
                    e.target?.stopDrag?.();
                  } catch (_) {
                    /* ignore */
                  }
                  return;
                }
                isDraggingPointRef.current = true;
                e.cancelBubble = true;
              }}
              onDragMove={(e) => {
                if (Date.now() < suppressPointDragUntilRef.current) {
                  e.cancelBubble = true;
                  e.target?.stopDrag?.();
                  return;
                }
                e.cancelBubble = true;
                isDraggingPointRef.current = true;

                let localX = e.target.x();
                let localY = e.target.y();

                const groupX = groupRef.current?.x() ?? element.x ?? 0;
                const groupY = groupRef.current?.y() ?? element.y ?? 0;

                if (!isOffCanvas && canvasWidth && canvasHeight) {
                  const canvasPadding = MAIN_STAGE_EDGE_BUFFER_PX;
                  const absPointX = localX + groupX;
                  const absPointY = localY + groupY;

                  const constrainedAbsX = Math.max(
                    canvasPadding,
                    Math.min(canvasWidth - canvasPadding, absPointX),
                  );
                  const constrainedAbsY = Math.max(
                    canvasPadding,
                    Math.min(canvasHeight - canvasPadding, absPointY),
                  );

                  localX = constrainedAbsX - groupX;
                  localY = constrainedAbsY - groupY;

                  e.target.x(localX);
                  e.target.y(localY);
                } else if (isOffCanvas) {
                  const canvasPadding = MAIN_STAGE_EDGE_BUFFER_PX;
                  const stage = groupRef.current?.getStage?.();
                  const stageWidth = stage?.width?.() || window.innerWidth;
                  const stageHeight =
                    stage?.height?.() || canvasHeight || window.innerHeight;
                  // Off-canvas has only left strip now; keep bend handles out of main column.
                  const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
                  const maxOffCanvasX = mainZoneStart || FIXED_OFF_CANVAS_SIDE_WIDTH_PX;
                  const absPointX = localX + groupX;
                  const absPointY = localY + groupY;
                  const constrainedAbsX = Math.max(
                    canvasPadding,
                    Math.min(maxOffCanvasX - canvasPadding, absPointX),
                  );
                  const constrainedAbsY = Math.max(
                    canvasPadding,
                    Math.min(stageHeight - canvasPadding, absPointY),
                  );
                  localX = constrainedAbsX - groupX;
                  localY = constrainedAbsY - groupY;
                  e.target.x(localX);
                  e.target.y(localY);
                }

                // Update point with scaling - this will update all points proportionally
                // The scaling logic will update all other circles, while this circle
                // stays at the dragged position (handled by Konva)
                updatePoint(index, localX, localY, e);
              }}
              onDragEnd={(e) => {
                if (Date.now() < suppressPointDragUntilRef.current) {
                  e.cancelBubble = true;
                  isDraggingPointRef.current = false;
                  e.target?.stopDrag?.();
                  return;
                }
                e.cancelBubble = true;

                let localX = e.target.x();
                let localY = e.target.y();

                const groupX = groupRef.current?.x() ?? element.x ?? 0;
                const groupY = groupRef.current?.y() ?? element.y ?? 0;

                if (!isOffCanvas && canvasWidth && canvasHeight) {
                  const canvasPadding = MAIN_STAGE_EDGE_BUFFER_PX;
                  const absPointX = localX + groupX;
                  const absPointY = localY + groupY;

                  const constrainedAbsX = Math.max(
                    canvasPadding,
                    Math.min(canvasWidth - canvasPadding, absPointX),
                  );
                  const constrainedAbsY = Math.max(
                    canvasPadding,
                    Math.min(canvasHeight - canvasPadding, absPointY),
                  );

                  localX = constrainedAbsX - groupX;
                  localY = constrainedAbsY - groupY;

                  e.target.x(localX);
                  e.target.y(localY);
                } else if (isOffCanvas) {
                  const canvasPadding = MAIN_STAGE_EDGE_BUFFER_PX;
                  const stage = groupRef.current?.getStage?.();
                  const stageWidth = stage?.width?.() || window.innerWidth;
                  const stageHeight =
                    stage?.height?.() || canvasHeight || window.innerHeight;
                  // Off-canvas has only left strip now; keep bend handles out of main column.
                  const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
                  const maxOffCanvasX = mainZoneStart || FIXED_OFF_CANVAS_SIDE_WIDTH_PX;
                  const absPointX = localX + groupX;
                  const absPointY = localY + groupY;
                  const constrainedAbsX = Math.max(
                    canvasPadding,
                    Math.min(maxOffCanvasX - canvasPadding, absPointX),
                  );
                  const constrainedAbsY = Math.max(
                    canvasPadding,
                    Math.min(stageHeight - canvasPadding, absPointY),
                  );
                  localX = constrainedAbsX - groupX;
                  localY = constrainedAbsY - groupY;
                  e.target.x(localX);
                  e.target.y(localY);
                }

                // Final update with scaling
                updatePoint(index, localX, localY, e);

                if (saveToHistory) saveToHistory();

                // Sync all circle positions after drag ends
                setTimeout(() => {
                  if (groupRef.current) {
                    const circles = groupRef.current.find("Circle");
                    const finalPoints = currentPointsRef.current;
                    circles.forEach((circle, circleIndex) => {
                      const pointIndex = circleIndex * 2;
                      if (pointIndex < finalPoints.length - 1) {
                        circle.x(finalPoints[pointIndex]);
                        circle.y(finalPoints[pointIndex + 1]);
                      }
                    });
                    groupRef.current.getLayer()?.batchDraw();
                  }
                  isDraggingPointRef.current = false;
                }, 50);
              }}
              onDblClick={(e) => {
                const pts = currentPointsRef.current || [];
                const len = pts.length;
                const isInteriorBend = len > 4 && index > 1 && index < len - 2;
                if (!isInteriorBend) return;
                e.cancelBubble = true;
                e.evt?.stopPropagation?.();
                try {
                  e.target?.stopDrag?.();
                } catch (_) {
                  /* ignore */
                }
                stopAllControlPointDrags();
                removeBendPoint(index, e);
              }}
              onDblTap={(e) => {
                const pts = currentPointsRef.current || [];
                const len = pts.length;
                const isInteriorBend = len > 4 && index > 1 && index < len - 2;
                if (!isInteriorBend) return;
                e.cancelBubble = true;
                e.evt?.stopPropagation?.();
                try {
                  e.target?.stopDrag?.();
                } catch (_) {
                  /* ignore */
                }
                stopAllControlPointDrags();
                removeBendPoint(index, e);
              }}
            />
          ) : null,
        )}

      {/* Copy button for off-canvas elements */}
      {(() => {
        // Copy button commented out
        const shouldShowCopyButton =
          false &&
          isSelected &&
          !isPreview &&
          isOffCanvas &&
          typeof onCopyToOppositeCanvas === "function";

        if (!shouldShowCopyButton) return null;

        if (!currentPoints || currentPoints.length < 4) return null;
        const xCoords = currentPoints.filter((_, i) => i % 2 === 0);
        const yCoords = currentPoints.filter((_, i) => i % 2 === 1);
        const minX = Math.min(...xCoords);
        const maxX = Math.max(...xCoords);
        const minY = Math.min(...yCoords);
        const maxY = Math.max(...yCoords);

        const copyButtonX = maxX - 14;
        const copyButtonY = minY - 36;

        return (
          <Html
            x={copyButtonX}
            y={copyButtonY}
            divProps={{
              style: {
                pointerEvents: "none",
                zIndex: 30,
              },
            }}
          >
            <div style={{ pointerEvents: "none" }}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCopyToOppositeCanvas?.();
                }}
                title="Copy to Main Canvas"
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "4px",
                  border: "1px solid #CBD5E1",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.18)",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  padding: 0,
                  transition:
                    "background-color 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F1F5F9";
                  e.currentTarget.style.borderColor = "#94A3B8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.borderColor = "#CBD5E1";
                }}
              >
                <svg
                  width="16"
                  height="19"
                  viewBox="0 0 17 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.97572 15.75C5.47055 15.75 5.04297 15.575 4.69297 15.225C4.34297 14.875 4.16797 14.4474 4.16797 13.9423V2.55775C4.16797 2.05258 4.34297 1.625 4.69297 1.275C5.04297 0.925 5.47055 0.75 5.97572 0.75H14.3602C14.8654 0.75 15.293 0.925 15.643 1.275C15.993 1.625 16.168 2.05258 16.168 2.55775V13.9423C16.168 14.4474 15.993 14.875 15.643 15.225C15.293 15.575 14.8654 15.75 14.3602 15.75H5.97572ZM5.97572 14.25H14.3602C14.4372 14.25 14.5077 14.2179 14.5717 14.1538C14.6359 14.0898 14.668 14.0192 14.668 13.9423V2.55775C14.668 2.48075 14.6359 2.41025 14.5717 2.34625C14.5077 2.28208 14.4372 2.25 14.3602 2.25H5.97572C5.89872 2.25 5.82822 2.28208 5.76422 2.34625C5.70005 2.41025 5.66797 2.48075 5.66797 2.55775V13.9423C5.66797 14.0192 5.70005 14.0898 5.76422 14.1538C5.82822 14.2179 5.89872 14.25 5.97572 14.25ZM2.47572 19.25C1.97055 19.25 1.54297 19.075 1.19297 18.725C0.842969 18.375 0.667969 17.9474 0.667969 17.4423V4.55775H2.16797V17.4423C2.16797 17.5193 2.20005 17.5898 2.26422 17.6538C2.32822 17.7179 2.39872 17.75 2.47572 17.75H12.3602V19.25H2.47572Z"
                    fill="#1E293B"
                  />
                </svg>
              </button>
            </div>
          </Html>
        );
      })()}
    </Group>
  );
};

UnifiedLineArrowElement.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    points: PropTypes.arrayOf(PropTypes.number).isRequired,
    strokeColor: PropTypes.string,
    strokeWidth: PropTypes.number,
    isDashed: PropTypes.bool,
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    rotation: PropTypes.number,
    hasStartArrow: PropTypes.bool,
    hasEndArrow: PropTypes.bool,
  }).isRequired,
  isDraggable: PropTypes.bool,
  onClick: PropTypes.func,
  onSelect: PropTypes.func,
  onDragStart: PropTypes.func,
  isSelected: PropTypes.bool,
  onDragEnd: PropTypes.func.isRequired,
  saveToHistory: PropTypes.func,
  canvasWidth: PropTypes.number,
  canvasHeight: PropTypes.number,
  isMultiSelectMode: PropTypes.bool,
  selectedObjectsArray: PropTypes.array,
};

UnifiedLineArrowElement.defaultProps = {
  isDraggable: false,
  onClick: () => {},
  onSelect: () => {},
  isSelected: false,
  saveToHistory: () => {},
};

export default UnifiedLineArrowElement;
